if (!window.Maximize.loadedScript.includes("product-card.js")) {
  window.Maximize.loadedScript.push("product-card.js");

  class ProductCardSwatchOption extends HTMLElement {
    static cacheData = {};
    static instanceCount = 0;
    constructor() {
      super();
      if (this.hasAttribute("data-combined-listing")) return;
      this.productCardWrapperEle = this.closest(`.product-card__wrapper`);
      this.productId = this.dataset.productId;
      this.sectionId = this.dataset.sectionId;
      this.optionId = this.dataset.optionId;
      this.productCardId = this.dataset.productCardId;
      this.productUrl = this.dataset.productUrl;
      this.effectImage = this.dataset.effectImage;
      this.isShowEDT = this.hasAttribute("data-show-edt") && this.dataset.showEdt === "true";
      this.optionsSelectedValues = [];

      this.optionValueInputElements = Array.from(this.querySelectorAll(`.variant-option__input`));
      this.optionLabelElements = Array.from(this.querySelectorAll(`.swatch-option__label`));
      const listSelectedOptionValue = this.optionValueInputElements.filter((ele) => ele.classList.contains("selected"));
      listSelectedOptionValue.forEach((input) => {
        const position = input.dataset.optionPosition;
        this.optionsSelectedValues[position - 1] = input.dataset.optionValueId;
      });
    }

    connectedCallback() {
      if (this.hasAttribute("data-combined-listing")) {
        this.productCardWrapperEle = this.closest("[data-native-product-card]");
        if (!this.productCardWrapperEle) return;
        this.instanceId ||= `native-card-${++ProductCardSwatchOption.instanceCount}`;
        this.listeners?.abort();
        this.listeners = new AbortController();
        this.addEventListener(
          "click",
          (event) => {
            const option = event.target.closest("button[data-option-value-id]");
            if (!option || option.dataset.available === "false") return;
            event.preventDefault();
            this.switchCard(option);
          },
          { signal: this.listeners.signal },
        );
        const preventPendingPurchase = (event) => {
          if (this.productCardWrapperEle.hasAttribute("data-product-switch-pending") && !this.contains(event.target)) {
            event.preventDefault();
            event.stopImmediatePropagation();
          }
        };
        for (const type of ["click", "submit"]) {
          this.productCardWrapperEle.addEventListener(type, preventPendingPurchase, {
            capture: true,
            signal: this.listeners.signal,
          });
        }
        return;
      }
      this.optionLabelElements.forEach((el) => {
        el.addEventListener("click", this.handleClickOptionLabel.bind(this));

        el.addEventListener("keyup", (el) => {
          if (el.key === "Enter" || el.key === " ") {
            this.handleClickOptionLabel.bind(this);
          }
        });
      });
    }

    disconnectedCallback() {
      this.request?.abort();
      this.listeners?.abort();
      this.finishCardRequest();
    }

    finishCardRequest() {
      this.pendingSelection = null;
      this.pendingControls?.forEach(([control, inert]) => {
        control.inert = inert;
      });
      this.pendingControls = null;
      this.productCardWrapperEle?.removeAttribute("data-product-switch-pending");
      this.productCardWrapperEle?.removeAttribute("aria-busy");
    }

    cardProductUrl(value) {
      const url = new URL(value, window.location.href);
      if (
        url.origin !== window.location.origin ||
        !/^https?:$/.test(url.protocol) ||
        !url.pathname.includes("/products/")
      )
        throw new Error("Invalid card product URL");
      if (url.pathname.startsWith("/products/"))
        url.pathname = (window.Shopify?.routes?.root || "/").replace(/\/$/, "") + url.pathname;
      return url;
    }

    async switchCard(option) {
      const card = this.productCardWrapperEle;
      // A cart request already owns the displayed SKU until it completes.
      if (Array.from(card.querySelectorAll("[x-data]")).some((element) => window.Alpine?.$data(element)?.isLoading))
        return;
      this.request?.abort();
      const request = new AbortController();
      this.request = request;
      this.pendingControls ||= Array.from(
        card.querySelectorAll("form, .ms-top-card__cart--link, .product-card__widget"),
        (control) => [control, control.inert],
      );
      this.pendingControls.forEach(([control]) => {
        control.inert = true;
      });
      card.dataset.productSwitchPending = "true";
      card.setAttribute("aria-busy", "true");
      this.querySelector('[role="status"]').textContent = "";
      try {
        this.pendingSelection ||= {
          values: new Map(
            Array.from(this.querySelectorAll('[data-option-value-id][aria-pressed="true"]'))
              .filter((value) => !value.closest("[hidden]"))
              .map((value) => [Number(value.dataset.optionPosition), value.dataset.optionValueId]),
          ),
          productUrl: this.dataset.productUrl,
          urlPosition: Infinity,
        };
        const position = Number(option.dataset.optionPosition);
        this.pendingSelection.values.set(position, option.dataset.optionValueId);
        // Later options still carry URLs from the old DOM while an earlier choice is loading.
        if (position <= this.pendingSelection.urlPosition) {
          this.pendingSelection.productUrl = option.dataset.productUrl || this.pendingSelection.productUrl;
          this.pendingSelection.urlPosition = position;
        }
        const url = this.cardProductUrl(this.pendingSelection.productUrl);
        const optionValues = Array.from(this.pendingSelection.values)
          .sort(([left], [right]) => left - right)
          .map(([, value]) => value);
        url.searchParams.delete("variant");
        url.searchParams.set("option_values", optionValues.join(","));
        const cardType = card.dataset.nativeProductCard;
        if (!["mediashop", "mediashop-highlight", "vendor"].includes(cardType)) throw new Error("Invalid card type");
        // Render only this card format; keep product-swatch for legacy swatches.
        url.searchParams.set("section_id", `ms-card-${cardType}`);
        const response = await fetch(url.href, { signal: request.signal, credentials: "same-origin" });
        if (!response.ok) throw new Error(`Card product response ${response.status}`);
        if (response.url && new URL(response.url).origin !== window.location.origin)
          throw new Error("Invalid card response origin");
        const html = await response.text();
        if (request.signal.aborted || this.request !== request || !this.isConnected) return;
        const page = new DOMParser().parseFromString(html, "text/html");
        const template = page.querySelector(`template[data-native-card-template="${card.dataset.nativeProductCard}"]`);
        const next = template?.content.querySelector("[data-native-product-card]");
        const picker = next?.querySelector("[data-combined-listing]");
        const state = next?.querySelector("[data-native-card-state]");
        const variant = state && JSON.parse(state.textContent);
        const inventory = JSON.parse(next?.querySelector("[data-native-card-inventory]")?.textContent || "{}");
        const nextUrl = next && this.cardProductUrl(next.dataset.productUrl);
        if (
          !next ||
          !picker ||
          !state ||
          !next.dataset.productId ||
          nextUrl.origin !== window.location.origin ||
          (variant !== null && !variant?.id)
        ) {
          throw new Error("Incomplete card product response");
        }
        next.dataset.productUrl = nextUrl.pathname;
        for (const part of ["media", "title", "price"]) {
          if (!next.querySelector(`[data-card-part="${part}"]`)) throw new Error("Incomplete card content");
        }
        for (const value of optionValues) {
          if (!picker.querySelector(`[data-option-value-id="${CSS.escape(value)}"][aria-pressed="true"]`))
            throw new Error("Card selection was not preserved");
        }
        const restoreFocus = this.contains(document.activeElement);
        const focusValue = option.dataset.optionValueId;
        const focusPosition = option.dataset.optionPosition;
        const imageSizes = card.querySelector('[data-card-part="media"] img')?.sizes;
        if (imageSizes)
          next.querySelectorAll('[data-card-part="media"] img').forEach((img) => {
            img.sizes = imageSizes;
          });
        for (const selector of [".price__content", ".price-wrapper"]) {
          const currentPrice = card.querySelector(selector);
          const nextPrice = next.querySelector(selector);
          if (!currentPrice || !nextPrice) continue;
          const layoutClasses = (className) => /(^|:)justify-|(^|:)h[1-6]$/.test(className);
          nextPrice.classList.remove(...Array.from(nextPrice.classList).filter(layoutClasses));
          nextPrice.classList.add(...Array.from(currentPrice.classList).filter(layoutClasses));
        }
        const idMap = new Map();
        next.querySelectorAll("[id]").forEach((element) => {
          const id = element.getAttribute("id");
          idMap.set(id, `${this.instanceId}-${id}`);
          element.setAttribute("id", idMap.get(id));
        });
        next.querySelectorAll("[for], [aria-labelledby], [aria-describedby], [aria-controls]").forEach((element) => {
          for (const attribute of ["for", "aria-labelledby", "aria-describedby", "aria-controls"]) {
            if (element.hasAttribute(attribute))
              element.setAttribute(
                attribute,
                element
                  .getAttribute(attribute)
                  .split(" ")
                  .map((id) => idMap.get(id) || id)
                  .join(" "),
              );
          }
        });
        // Validate and prepare every replacement before mutating the displayed product.
        const replacements = Array.from(card.querySelectorAll("[data-card-part]"), (target) => {
          const source = next.querySelector(`[data-card-part="${target.dataset.cardPart}"]`);
          const content = document.createDocumentFragment();
          if (source) content.append(...Array.from(source.childNodes, (node) => node.cloneNode(true)));
          const form = target.closest("form");
          if (form)
            content.querySelectorAll("[form]").forEach((input) => input.setAttribute("form", form.getAttribute("id")));
          // This component caches its children in its constructor. Upgrade a fresh inert clone on insertion.
          const replacement = target.matches("custom-tooltip-container") && source ? source.cloneNode(true) : null;
          if (replacement)
            for (const attribute of target.attributes) replacement.setAttribute(attribute.name, attribute.value);
          return { target, source, content, replacement };
        });
        const forms = Array.from(card.querySelectorAll("[x-data]"))
          .filter((element) => element.getAttribute("x-data").startsWith("xProductForm("))
          .map((element) => window.Alpine?.$data(element));
        if (forms.some((form) => typeof form?.handleAddToCart !== "function"))
          throw new Error("Card form is not ready");
        const cardState = card.hasAttribute("x-data") ? window.Alpine?.$data(card) : null;
        if (card.hasAttribute("x-data") && typeof cardState?.showOptions !== "boolean")
          throw new Error("Card is not ready");
        const quantities = Array.from(card.querySelectorAll('[x-data^="xQuantitySelectorAtc("]'), (element) => ({
          element,
          state: window.Alpine?.$data(element),
        }));
        if (quantities.some((quantity) => typeof quantity.state?.plus !== "function"))
          throw new Error("Card quantity is not ready");
        for (const { target, source, content, replacement } of replacements) {
          if (replacement) target.replaceWith(replacement);
          else target.replaceChildren(content);
          if (target.dataset.cardPart === "media") {
            for (const attribute of ["aria-label", "x-labels-data"]) {
              if (attribute === "x-labels-data" && !target.hasAttribute(attribute)) continue;
              if (source?.hasAttribute(attribute)) target.setAttribute(attribute, source.getAttribute(attribute));
              else target.removeAttribute(attribute);
            }
          }
        }
        for (const key of ["productId", "variantId", "productUrl", "productSku", "productHandle"])
          card.dataset[key] = next.dataset[key] || "";
        if (cardState) {
          cardState.cardProductId = next.dataset.productId;
          cardState.cardProductHandle = next.dataset.productHandle;
        }
        card.dataset.currentVariantId = variant?.id || "";
        card.classList.toggle("ms-top-card--sale-border", next.classList.contains("ms-top-card--sale-border"));
        card
          .querySelector(".ms-top-card__delivery")
          ?.classList.toggle("ms-top-card__delivery--unavailable", !variant?.available);
        const productLink = nextUrl;
        if (variant) productLink.searchParams.set("variant", variant.id);
        else productLink.searchParams.set("option_values", optionValues.join(","));
        card
          .querySelectorAll(
            "a.link-product-variant, .ms-top-card__media-link, .ms-top-card__title a, .ms-top-card__cart--link",
          )
          .forEach((link) => {
            let previousUrl;
            try {
              previousUrl = new URL(link.getAttribute("href"), window.location.href);
            } catch {
              return;
            }
            if (previousUrl.origin === window.location.origin && previousUrl.pathname.includes("/products/"))
              link.setAttribute("href", productLink.pathname + productLink.search);
          });
        card.querySelectorAll(".current-variant").forEach((element) => {
          element.textContent = variant?.id || "";
        });
        card.querySelectorAll('[data-type="init-current-variant"], [data-native-card-state]').forEach((element) => {
          element.textContent = JSON.stringify(variant);
        });
        card.querySelectorAll("[data-native-card-inventory], .product-card-variant-inventory").forEach((element) => {
          element.textContent = JSON.stringify(inventory);
        });
        card.querySelectorAll("[data-variant-id]").forEach((element) => {
          element.dataset.variantId = variant?.id || "";
        });
        forms.forEach((form) => {
          form.currentVariantAdditionalInfo = inventory;
          form.variantId = variant?.id || "";
          form.isOptionAvailable = !!variant?.available;
          form.isOptionExist = !!variant;
          form.isAvailableButton = !!variant?.available;
          form.currentVariant = variant;
        });
        card.querySelectorAll('input[name="id"]').forEach((input) => {
          input.value = variant?.id || "";
          input.disabled = !variant?.available;
        });
        card.querySelectorAll('input[name="product-id"]').forEach((input) => {
          input.value = next.dataset.productId;
        });
        quantities.forEach(({ element, state: quantity }) => {
          quantity.productId = Number(next.dataset.productId);
          quantity.qty = 1;
          const input = element.querySelector('[name="quantity"]');
          if (input) input.disabled = !variant?.available;
          const wrapper = element.closest(".product-card__quantity-wrapper");
          for (const className of ["opacity-40", "pointer-events-none", "cursor-not-allowed"])
            wrapper?.classList.toggle(className, !variant?.available);
        });
        if (!variant) card.querySelector('[data-card-part="price"]').replaceChildren();
        this.replaceChildren(...Array.from(picker.childNodes, (node) => node.cloneNode(true)));
        this.dataset.productUrl = next.dataset.productUrl;
        card.dataset.listOptionValuesIdSelected = Array.from(
          this.querySelectorAll('[aria-pressed="true"]'),
          (value) => value.dataset.optionValueId,
        ).join(",");
        if (restoreFocus) {
          const focus =
            this.querySelector(`[data-option-value-id="${CSS.escape(focusValue)}"]`) ||
            this.querySelector(`[data-option-position="${CSS.escape(focusPosition)}"][aria-pressed="true"]`);
          focus?.focus({ preventScroll: true });
        }
        const labelLink = card.querySelector("[x-labels-data]");
        try {
          if (labelLink) window.Alpine?.store("xLabelsAndBadges")?.load(labelLink);
        } catch (error) {
          console.warn("Card labels could not initialize", error);
        }
        try {
          if (card.querySelector(".pickup-message__wrapper")) {
            const store =
              window.Alpine?.store("xStoreMap")?.currentStoreSelected ??
              JSON.parse(localStorage.getItem("currentStoreSelected") || "null")?.selectedStore ??
              "";
            document.dispatchEvent(new CustomEvent("maximize:store-changed", { detail: { store, root: card } }));
          }
        } catch (error) {
          console.warn("Card pickup message could not initialize", error);
        }
      } catch (error) {
        if (!request.signal.aborted && this.isConnected && this.request === request) {
          this.querySelector('[role="status"]').textContent = this.dataset.errorMessage;
          console.error(error);
        }
      } finally {
        if (this.request === request) {
          this.finishCardRequest();
          this.request = null;
        }
      }
    }

    handleClickOptionLabel(event) {
      event.preventDefault();
      const optionLabel = event.currentTarget;
      const option = optionLabel.previousElementSibling;
      const position = option.dataset.optionPosition;
      const idx = position - 1;
      let listInputSamePosition = this.optionValueInputElements.filter((el) => el.dataset.optionPosition === position);
      listInputSamePosition.forEach((el) => {
        if (el.dataset.optionValueId !== option.dataset.optionValueId) {
          el.checked = false;
          el.classList.remove("selected");
        } else {
          el.checked = true;
          el.classList.add("selected");
          this.optionsSelectedValues[idx] = el.dataset.optionValueId;
        }
      });
      this.reRenderProducSwatchs();
    }

    async reRenderProducSwatchs() {
      this.productCardWrapperEle.dataset.listOptionValuesIdSelected = this.optionsSelectedValues.join(",");
      const url = `${this.productUrl}?option_values=${this.optionsSelectedValues.join(",")}&section_id=product-swatch`;
      let dataHTML = ProductCardSwatchOption.cacheData[url];
      if (!dataHTML) {
        try {
          const response = await fetch(url);
          const text = await response.text();
          dataHTML = new DOMParser().parseFromString(text, "text/html");
          ProductCardSwatchOption.cacheData[url] = dataHTML;
        } catch (error) {
          console.error(error);
          return;
        }
      }

      this.updateElements(this.productCardWrapperEle, dataHTML, ".current-variant");
      if (this.isShowEDT) {
        this.updateElements(this.productCardWrapperEle, dataHTML, ".estimate-delivery-card-msg");
      }
      this.updateLabelData(this.productCardWrapperEle, dataHTML);
      this.renderSwatchOptionStyle(dataHTML, this.productCardWrapperEle);
      this.updateImage(dataHTML);
    }

    updateLabelData(rootEl, dataHTML) {
      const metafieldDataset = dataHTML.querySelector("#product-swatch")?.dataset.metafieldLabel;
      if (!metafieldDataset) return;

      const linkVariantEle = rootEl.querySelector(".link-product-variant");
      if (!linkVariantEle) return;

      const metafieldDataObj = xParseJSON(metafieldDataset);
      const currentLabelData = xParseJSON(linkVariantEle.getAttribute("x-labels-data"));

      currentLabelData.metafield_label = metafieldDataObj.metafield_label;

      linkVariantEle.setAttribute("x-labels-data", JSON.stringify(currentLabelData));
      Alpine.store("xLabelsAndBadges")?.load(linkVariantEle);
    }

    updateElements(rootEl, dataHTML, selector) {
      const oldElement = rootEl.querySelector(`${selector}`);
      const newElement = dataHTML.querySelector(`${selector}`);

      if (oldElement && newElement) {
        oldElement.innerHTML = newElement.innerHTML;
      }
    }

    renderSwatchOptionStyle(dataHTML, productCardWrapper) {
      const labels = productCardWrapper.querySelectorAll(".swatch-option__label");

      labels.forEach((label) => {
        const dataIndex = label.dataset.position;
        const sourceLabel = dataHTML.querySelector(`.swatch-option__label[data-position="${dataIndex}"]`);

        if (sourceLabel?.hasAttribute("style")) {
          const sourceStyle = sourceLabel.getAttribute("style");
          label.setAttribute("style", sourceStyle);
        }

        const input = label.previousElementSibling;
        const newInput = sourceLabel.previousElementSibling;

        if (input && newInput) {
          input.classList[newInput.classList.contains("disabled") ? "add" : "remove"]("disabled");
          input.classList[newInput.classList.contains("selected") ? "add" : "remove"]("selected");
          input.checked = newInput.classList.contains("selected");
        }
      });
    }

    updateImage(dataHTML) {
      const img = this.productCardWrapperEle.querySelector(".slide-item.is-active .product-card__image");
      let newImg = dataHTML?.querySelector(`.product-card__image`);
      const activeSlide = this.productCardWrapperEle.querySelector(".slide-item.is-active");
      let newMediaId = null;
      if (newImg) {
        newMediaId = newImg.dataset.mediaId;
      }
      if (this.effectImage == "carousel") {
        const slideToActive = this.productCardWrapperEle.querySelector(`.slide-item[data-media-id="${newMediaId}"]`);
        if (activeSlide && slideToActive) {
          activeSlide.classList.remove("is-active");
          slideToActive.classList.add("is-active");
          return;
        }
      }

      if (img && newImg) {
        img.setAttribute("srcset", newImg.getAttribute("srcset"));
        img.setAttribute("src", newImg.getAttribute("src"));
        if (activeSlide && this.effectImage == "carousel") {
          activeSlide.dataset.mediaId = newMediaId;
        }
      }
    }
  }

  if (!customElements.get("product-card-swatch-option")) {
    customElements.define("product-card-swatch-option", ProductCardSwatchOption);
  }

  class ChooseOptionElement extends HTMLElement {
    static cacheData = {};
    constructor() {
      super();
      this.optionInputElements = Array.from(this.querySelectorAll(`.swatch-option__value`));
      this.optionLabelElements = Array.from(this.querySelectorAll(`.swatch-option__label`));
      this.optionsSelectedValues = [];
      this.productId = this.dataset.productId;
      this.productUrl = this.dataset.productUrl;
      this.chooseOptionElementId = this.dataset.chooseOptionElementId;
      const listSelectedOptionValue = this.optionInputElements.filter((ele) => ele.classList.contains("selected"));
      listSelectedOptionValue.forEach((input) => {
        const position = input.dataset.optionPosition;
        this.optionsSelectedValues[position - 1] = input.dataset.optionValueId;
      });

      this.currentVariantInfoEle = this.querySelector('[type="application/json"][data-type="current-variant-info"]');
      this.currentVariantAdditionalInfoEle = this.querySelector(
        '[type="application/json"][data-type="current-variant-additional-info"]',
      );
      if (this.currentVariantInfoEle) {
        this.currentVariant = JSON.parse(this.currentVariantInfoEle?.textContent);
      }
    }

    connectedCallback() {
      this.optionLabelElements.forEach((el) => {
        el.addEventListener("click", this.handleClickOptionLabel.bind(this));

        el.addEventListener("keyup", (e) => {
          if (e.key === "Enter" || e.key === " ") {
            this.handleClickOptionLabel.call(this, e);
          }
        });
      });
    }

    handleClickOptionLabel(event) {
      event.preventDefault();
      const optionLabel = event.currentTarget;
      const option = optionLabel.previousElementSibling;
      const position = option.dataset.optionPosition;
      const idx = position - 1;
      let listInputSamePosition = this.optionInputElements.filter((el) => el.dataset.optionPosition === position);
      const selectedValue = this.querySelector(`.seclected-value-${position}`);
      if (selectedValue) {
        selectedValue.textContent = option.dataset.value;
      }

      listInputSamePosition.forEach((el) => {
        if (el.dataset.optionValueId !== option.dataset.optionValueId) {
          el.checked = false;
          el.classList.remove("selected");
        } else {
          el.checked = true;
          el.classList.add("selected");
          this.optionsSelectedValues[idx] = el.dataset.optionValueId;
        }
      });

      this.reRenderChooseOption();
    }

    async reRenderChooseOption() {
      const variantPickerEle = this.closest(".variant-picker__wrapper");
      const indexParam = variantPickerEle?.dataset?.indexParam;
      const chooseOptionWrapper = this.closest(".choose-option");
      const buyButton = chooseOptionWrapper.querySelector(".product-form__btn-add-to-cart");
      if (buyButton) {
        buyButton.disabled = true;
      }
      const url = `${this.productUrl}?option_values=${this.optionsSelectedValues.join(",")}&section_id=choose-option&page=${indexParam}`;
      let dataHTML = ChooseOptionElement.cacheData[url];
      if (!dataHTML) {
        try {
          const response = await fetch(url);
          const text = await response.text();
          dataHTML = new DOMParser().parseFromString(text, "text/html");
          ChooseOptionElement.cacheData[url] = dataHTML;
        } catch (error) {
          console.error(error);
        }
      }

      this.updateElements(chooseOptionWrapper, dataHTML, ".choose-option__price-wrapper");
      this.updateElements(chooseOptionWrapper, dataHTML, ".choose-options-content .choose-option__properties-group");
      let newCurrentVariantInfoEle = dataHTML.querySelector(
        '[type="application/json"][data-type="current-variant-info"]',
      );
      if (newCurrentVariantInfoEle) {
        this.currentVariant = JSON.parse(newCurrentVariantInfoEle?.textContent);
        this.currentVariantInfoEle.textContent = newCurrentVariantInfoEle?.textContent;
      }
      let newCurrentVariantAdditionalInfoEle = dataHTML.querySelector(
        '[type="application/json"][data-type="current-variant-additional-info"]',
      );
      if (this.currentVariantAdditionalInfoEle && newCurrentVariantAdditionalInfoEle) {
        this.currentVariantAdditionalInfoEle.textContent = newCurrentVariantAdditionalInfoEle.textContent;
      }

      this.renderSwatchOptionStyle(dataHTML, chooseOptionWrapper);
      if (buyButton && !buyButton.ariaDisabled) {
        buyButton.disabled = false;
      }
      this.dispatchChangeVariantEvent();
    }

    updateElements(rootEl, dataHTML, selector) {
      const oldElement = rootEl.querySelector(`${selector}`);
      const newElement = dataHTML.querySelector(`${selector}`);

      if (oldElement && newElement) {
        oldElement.innerHTML = newElement.innerHTML;
      }
    }

    renderSwatchOptionStyle(dataHTML, productCardWrapper) {
      const labels = productCardWrapper.querySelectorAll(".swatch-option__label");

      labels.forEach((label) => {
        const dataIndex = label.dataset.position;
        const sourceLabel = dataHTML.querySelector(`.swatch-option__label[data-position="${dataIndex}"]`);

        if (sourceLabel?.hasAttribute("style")) {
          const sourceStyle = sourceLabel.getAttribute("style");
          label.setAttribute("style", sourceStyle);
        }

        const input = label.previousElementSibling;
        const newInput = sourceLabel.previousElementSibling;

        if (input && newInput) {
          input.classList[newInput.classList.contains("disabled") ? "add" : "remove"]("disabled");
          input.classList[newInput.classList.contains("selected") ? "add" : "remove"]("selected");
          input.checked = newInput.classList.contains("selected");
        }
      });
    }
    dispatchChangeVariantEvent() {
      document.dispatchEvent(
        new CustomEvent(`${PRODUCT_EVENT.swatchChange}${this.chooseOptionElementId}`, {
          bubbles: true,
          detail: {
            productId: this.productId,
            currentVariant: this.currentVariant,
          },
        }),
      );
    }
  }

  if (!customElements.get("choose-option")) {
    customElements.define("choose-option", ChooseOptionElement);
  }
}
